import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {
  EntityTypes,
  KYB_GUARANTOR_ELSEWHERE_LOCK_TOOLTIP,
  KYB_SIGNER_RESTRICTION_TOOLTIP,
  KYB_SOLE_TRADER_ROLE_LOCK_TOOLTIP,
  KybEntityResult,
  KybPerson,
  KybPersonRoleSelection,
  KybPersonRowState,
  kybBusinessRoleLabels,
  kybIsSelectedByDefault,
  kybIsPersonSignerEligible,
  kybIsSelectLocked,
  kybIsSignerEligible,
  kybIsSoleTraderRoleLocked,
  kybLinkedRowsOnCompany,
  kybSectionHeading,
  kybSelectLockTooltip,
} from '@portal-workspace/grow-shared-library';
import {KybLockTickComponent} from './kyb-lock-tick.component';

/** Emitted when a tick targets a stakeholder with no application individual yet, so the caller can capture their details before the tick is applied. */
export interface KybPersonDetailsRequired {
  person: KybPerson;
  /** The ticked role(s) to pre-select in the details dialog. */
  isGuarantor: boolean;
  isSigner: boolean;
}

/** Staged KYC/AML match for a KYB person, shown on the row until Save or Undo. */
export interface KybPersonResolution {
  kind: 'existing' | 'new';
  name: string;
}

const KYB_ROLE_SELECTION_TOOLTIP =
  'Clear the Guarantor and Signer ticks before deselecting this person.';

@Component({
  selector: 'kyb-entity-section',
  templateUrl: './kyb-entity-section.component.html',
  styleUrls: ['./kyb-entity-section.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatTooltipModule, KybLockTickComponent],
})
export class KybEntitySectionComponent {

  private _entity!: KybEntityResult;
  private _stagedRowStates: ReadonlyMap<number, KybPersonRowState> = new Map<number, KybPersonRowState>();

  @Input({required: true})
  set entity(value: KybEntityResult) {
    this._entity = value;
    this.applyStagedRowStates();
  }

  get entity(): KybEntityResult {
    return this._entity;
  }

  // The parent owns unsaved rows because this component is destroyed whenever its entity collapses.
  @Input({required: false})
  set stagedRowStates(value: ReadonlyMap<number, KybPersonRowState>) {
    this._stagedRowStates = value;
    this.applyStagedRowStates();
  }
  // Overrides the FrankieOne-provided entity.entityType (e.g. step3OnwardsOrganisationType).
  @Input({required: false}) entityType: EntityTypes | null = null;
  // Force the people table to render even for trust/partnership (used for the corporate-trustee
  // "Select signers" table, which reuses this table without its own heading).
  @Input({required: false}) forceShowPeople = false;
  @Input({required: false}) showHeader = true;
  // The KYC/AML tab renders this table under a row already titled with the entity name, so it keeps
  // the heading bar but drops the name line.
  @Input({required: false}) showEntityName = true;
  // Read-only until the reviewer puts the row into edit mode (KYC/AML tab).
  @Input({required: false}) readonly = false;
  // Key People only: offer Manual KYB when the completed run returned nobody to tick.
  @Input({required: false}) allowManualEntry = false;
  /**
   * Wizard lock affordances: a 25%+ owner is hard-ticked and greyed, and an ineligible signer is
   * disabled. The KYC/AML tab turns this off so every box stays selectable; unmet rules are shown
   * as messages under the table instead.
   */
  @Input({required: false}) useLockAffordances = true;
  // KYC/AML tab only: ticking a stakeholder with no application individual must first capture
  // their full details, so the tick is held back and personDetailsRequired is emitted instead.
  @Input({required: false}) requireDetailsForNewIndividuals = false;
  // Stakeholders whose guarantor tick was settled elsewhere on the application, because the same
  // individual is a guarantor through a row this table does not own. Empty for a caller that owns
  // every row for its people and so has nothing to reconcile against.
  @Input({required: false}) guarantorLockedStakeholderIds: ReadonlySet<number> = new Set<number>();
  @Output() personRoleChange: EventEmitter<KybPersonRoleSelection> = new EventEmitter<KybPersonRoleSelection>();
  @Output() personSelectedChange: EventEmitter<{ person: KybPerson; selected: boolean }> = new EventEmitter<{ person: KybPerson; selected: boolean }>();
  // The whole row after any tick, for callers that persist a row at a time rather than a full screen.
  @Output() personChange: EventEmitter<KybPersonRowState> = new EventEmitter<KybPersonRowState>();
  // A tick on a stakeholder with no individualId, when requireDetailsForNewIndividuals is on.
  @Output() personDetailsRequired: EventEmitter<KybPersonDetailsRequired> = new EventEmitter<KybPersonDetailsRequired>();
  @Output() personResolutionUndo: EventEmitter<KybPerson> = new EventEmitter<KybPerson>();
  @Output() manualEntry: EventEmitter<void> = new EventEmitter<void>();
  // KYC/AML tab only: staged existing-person / new-person choices, keyed by stakeholderId.
  @Input({required: false}) resolutions: ReadonlyMap<number, KybPersonResolution> = new Map<number, KybPersonResolution>();

  // User overrides for editable rows, keyed by stakeholderId; absent => use the type-based default.
  private selectionOverrides: Map<number, boolean> = new Map<number, boolean>();
  // A role forces Select on while it is present. Remember the row's own value so undoing the last
  // role in this editing session restores the selection that existed beforehand.
  private selectionBeforeRoles: Map<number, boolean> = new Map<number, boolean>();

  // Optional so the component still news up in unit tests without a change detector.
  constructor(@Optional() private cdr?: ChangeDetectorRef) {}

  /**
   * Undo a tick that was held back for details capture (dialog cancelled). The checkbox is a bound
   * control, so the model never changed on the intercepted tick; this clears any staged override and
   * nudges change detection so the box renders unticked again.
   */
  revertTick(person: KybPerson) {
    this.selectionOverrides.delete(person.stakeholderId);
    this.cdr?.markForCheck();
  }

  /** Apply a details-dialog confirmation onto the held-back tick so OnPush re-renders the box. */
  applyConfirmedTick(
    person: KybPerson,
    isSelected: boolean,
    isGuarantor: boolean,
    isSigner: boolean,
  ): void {
    const row = this.entity.people.find(candidate => candidate.stakeholderId === person.stakeholderId)
      ?? person;
    row.isGuarantor = isGuarantor;
    row.isSigner = isSigner;
    row.isSelected = isSelected;
    this.selectionOverrides.set(row.stakeholderId, isSelected);
    this.cdr?.markForCheck();
  }

  /** Applies a selection the parent mirrored from another row of the same person on this company. */
  applyMirroredSelection(stakeholderId: number, isSelected: boolean): void {
    const row = this._entity?.people.find(candidate => candidate.stakeholderId === stakeholderId);
    if (!row) {
      return;
    }
    row.isSelected = isSelected;
    this.selectionOverrides.set(stakeholderId, isSelected);
    this.selectionBeforeRoles.delete(stakeholderId);
    this.cdr?.markForCheck();
  }

  resolvedEntityType(): EntityTypes {
    return this.entityType ?? this.entity.entityType;
  }

  heading(): string {
    // The name is dropped rather than passed through when the caller already titles the block with
    // it: kybSectionHeading prefixes it for a non-primary entity, which would repeat that title.
    return kybSectionHeading(
      this.resolvedEntityType(),
      this.entity.isPrimary,
      this.showEntityName ? this.entity.entityName : undefined,
    );
  }

  // Trust/partnership entities capture people via the dedicated trustee/partner component,
  // so the KYB people table is not shown for them.
  showPeopleTable(): boolean {
    if (this.forceShowPeople) {
      return true;
    }
    const type = this.resolvedEntityType();
    return type !== 'trust' && type !== 'partnership';
  }

  showManualEntry(): boolean {
    return this.allowManualEntry && !this.readonly && !this.entity.people.length;
  }

  /** Another row of this company resolves to the same application person (linked, not name-matched). */
  hasLinkedAlias(person: KybPerson): boolean {
    return person.individualId != null
      && this.entity.people.some(other =>
        other.stakeholderId !== person.stakeholderId && other.individualId === person.individualId);
  }

  /** Names the person's other rows on this company; their roles instead when the names are identical. */
  linkedAliasTooltip(person: KybPerson): string {
    const others = kybLinkedRowsOnCompany(this.entity.people, person)
      .filter(row => row.stakeholderId !== person.stakeholderId)
      .map(row => this.fullName(row) === this.fullName(person) ? this.roleLabel(row) : this.fullName(row));
    return `Same person as ${others.join(' and ')}. Ticks change on both rows.`;
  }

  fullName(person: KybPerson): string {
    return `${person.givenName ?? ''} ${person.surname ?? ''}`.trim();
  }

  roleLabel(person: KybPerson): string {
    const labels = kybBusinessRoleLabels(person.roles);
    return labels.length ? labels.join(', ') : '-';
  }

  ownershipLabel(person: KybPerson): string {
    return person.ownershipPercent == null ? '-' : `${person.ownershipPercent}%`;
  }

  isSelectLocked(person: KybPerson): boolean {
    return this.useLockAffordances
      && kybIsSelectLocked(this.resolvedEntityType(), person.ownershipPercent);
  }

  // Disabled state only. Kept out of the isSelectLocked / isRoleLocked predicates because those also
  // decide which value a row displays, and a readonly row must still show what is actually saved.
  isSelectDisabled(person: KybPerson): boolean {
    return this.readonly || this.isSelectLocked(person) || this.isSelectHeldByRole(person);
  }

  isSelectHeldByRole(person: KybPerson): boolean {
    return person.isGuarantor || person.isSigner;
  }

  isGuarantorDisabled(person: KybPerson): boolean {
    return this.readonly || this.isRoleLocked(person);
  }

  isSignerDisabled(person: KybPerson): boolean {
    return this.readonly || this.isSignerLocked(person);
  }

  // A guarantor tick that is forced on and non-editable: the person is the sole trader, or already
  // guarantees through a row elsewhere on the application, which shares one isGuarantor flag with this row.
  isRoleLocked(person: KybPerson): boolean {
    return this.isSoleTraderRoleLocked() || this.guarantorLockedStakeholderIds.has(person.stakeholderId);
  }

  // Wizard only: the KYC/AML tab keeps staff edits unlocked.
  isSoleTraderRoleLocked(): boolean {
    return this.useLockAffordances && kybIsSoleTraderRoleLocked(this.resolvedEntityType());
  }

  signerChecked(person: KybPerson): boolean {
    return this.isSoleTraderRoleLocked() || person.isSigner;
  }

  // Only a director, secretary or office holder can sign for a company, under any row that is them.
  // Other entity types are unrestricted.
  isSignerEligible(person: KybPerson): boolean {
    return this.resolvedEntityType() === 'company' ? kybIsPersonSignerEligible(this.entity.people, person) : true;
  }

  /**
   * The linked row this person signs through, when this row's own roles could not sign. The tick is
   * the person's one signer flag, so it is shown here but changed on that row.
   */
  signingRowFor(person: KybPerson): KybPerson | null {
    if (this.resolvedEntityType() !== 'company' || kybIsSignerEligible(person.roles)) {
      return null;
    }
    return kybLinkedRowsOnCompany(this.entity.people, person).find(row => kybIsSignerEligible(row.roles)) ?? null;
  }

  signingRoleLabel(row: KybPerson): string {
    return row.roles.isDirector ? 'director' : row.roles.isSecretary ? 'company secretary' : 'office holder';
  }

  selectTooltip(person: KybPerson): string {
    if (this.isSelectLocked(person)) {
      return kybSelectLockTooltip(this.resolvedEntityType(), person.ownershipPercent);
    }
    return this.isSelectHeldByRole(person) ? KYB_ROLE_SELECTION_TOOLTIP : '';
  }

  guarantorTooltip(person: KybPerson): string {
    if (this.isSoleTraderRoleLocked()) {
      return KYB_SOLE_TRADER_ROLE_LOCK_TOOLTIP;
    }
    return this.isRoleLocked(person) ? KYB_GUARANTOR_ELSEWHERE_LOCK_TOOLTIP : '';
  }

  signerTooltip(person: KybPerson): string {
    if (this.isSoleTraderRoleLocked()) {
      return KYB_SOLE_TRADER_ROLE_LOCK_TOOLTIP;
    }
    const signingRow = person.isSigner ? this.signingRowFor(person) : null;
    if (signingRow) {
      return `Signs as ${this.signingRoleLabel(signingRow)} on the ${this.fullName(signingRow)} row; change it there.`;
    }
    return this.isSignerLocked(person) ? KYB_SIGNER_RESTRICTION_TOOLTIP : '';
  }

  // A row that cannot sign in its own right can still tick the person as a signer; once they sign,
  // the tick is held by the row whose role it is, so it is changed there.
  isSignerLocked(person: KybPerson): boolean {
    return this.isSoleTraderRoleLocked()
      || (person.isSigner && !!this.signingRowFor(person))
      || (this.useLockAffordances && !this.isSignerEligible(person) && !person.isSigner);
  }

  isSelected(person: KybPerson): boolean {
    if (this.isSelectLocked(person)) {
      return kybIsSelectedByDefault(this.resolvedEntityType(), person.ownershipPercent);
    }
    // Any role tick forces the person selected (see syncSelectWithRoles); deriving it here means a
    // guarantor mirrored from the same person's row on another entity also shows selected.
    if (person.isGuarantor || person.isSigner) {
      return true;
    }
    return this.selectionOverrides.get(person.stakeholderId) ?? this.savedOrDefaultSelected(person);
  }

  /**
   * What a previous save recorded, else the entity-type default.
   *
   * Deliberately blind to the role ticks, which isSelected and syncSelectWithRoles each account for
   * themselves: reading them here would report a row as already selected the moment a role was
   * ticked on it, and syncSelectWithRoles would then tell the parent nothing.
   */
  private savedOrDefaultSelected(person: KybPerson): boolean {
    return person.isSelected ?? kybIsSelectedByDefault(this.resolvedEntityType(), person.ownershipPercent);
  }

  resolutionFor(person: KybPerson): KybPersonResolution | null {
    return this.resolutions.get(person.stakeholderId) ?? null;
  }

  onUndoResolution(person: KybPerson): void {
    this.personResolutionUndo.emit(person);
  }

  onSelectedChange(person: KybPerson, checked: boolean) {
    if (this.isSelectDisabled(person)) {
      return;
    }
    if (!checked && this.resolutionFor(person)) {
      this.personResolutionUndo.emit(person);
      return;
    }
    if (this.detailsRequiredForTick(person, checked)) {
      this.personDetailsRequired.emit({ person, isGuarantor: person.isGuarantor, isSigner: person.isSigner });
      return;
    }
    // Keep the row's explicit value current so a guarantor mirrored onto this person can restore
    // a selection changed during this editing session, not only the value from the previous save.
    person.isSelected = checked;
    this.selectionOverrides.set(person.stakeholderId, checked);
    this.personSelectedChange.emit({person, selected: checked});
    this.emitRowState(person);
    this.mirrorToLinkedRows(person);
  }

  onGuarantorChange(person: KybPerson, checked: boolean) {
    if (this.isGuarantorDisabled(person)) {
      return;
    }
    if (this.detailsRequiredForTick(person, checked)) {
      this.personDetailsRequired.emit({ person, isGuarantor: true, isSigner: person.isSigner });
      return;
    }
    person.isGuarantor = checked;
    this.syncSelectWithRoles(person);
    this.emit(person);
    this.mirrorToLinkedRows(person);
  }

  onSignerChange(person: KybPerson, checked: boolean) {
    if (this.isSignerDisabled(person)) {
      return;
    }
    if (this.detailsRequiredForTick(person, checked)) {
      this.personDetailsRequired.emit({ person, isGuarantor: person.isGuarantor, isSigner: true });
      return;
    }
    person.isSigner = checked;
    this.syncSelectWithRoles(person);
    this.emit(person);
    this.mirrorToLinkedRows(person);
  }

  // Linked rows of one person on this company share one role and one guarantor flag, so a tick on
  // one row is the person's tick: every row shows it, and each is emitted so the caller stages the
  // same state for all of them rather than letting the save pick between rows that disagree.
  private mirrorToLinkedRows(source: KybPerson): void {
    const aliases = kybLinkedRowsOnCompany(this.entity.people, source)
      .filter(row => row.stakeholderId !== source.stakeholderId);
    if (!aliases.length) {
      return;
    }
    const selected = this.isSelected(source);
    for (const alias of aliases) {
      alias.isGuarantor = source.isGuarantor;
      alias.isSigner = source.isSigner;
      if (!this.isSelectLocked(alias)) {
        alias.isSelected = selected;
        this.selectionOverrides.set(alias.stakeholderId, selected);
        this.selectionBeforeRoles.delete(alias.stakeholderId);
      }
      this.personSelectedChange.emit({person: alias, selected: this.isSelected(alias)});
      this.emit(alias);
    }
    this.cdr?.markForCheck();
  }

  // Only the first tick for a stakeholder with no application individual needs details. A staged
  // existing/new-person resolution means the dialog has already captured that identity.
  private detailsRequiredForTick(person: KybPerson, checked: boolean): boolean {
    return this.requireDetailsForNewIndividuals
      && checked
      && person.individualId == null
      && !this.resolutionFor(person);
  }

  private applyStagedRowStates(): void {
    if (!this._entity) {
      return;
    }
    for (const person of this._entity.people) {
      const state = this._stagedRowStates.get(person.stakeholderId);
      if (!state) {
        continue;
      }
      person.isSelected = state.isSelected;
      person.isGuarantor = state.isGuarantor;
      person.isSigner = state.isSigner;
    }
  }

  // Keep the left-column select in step with the role ticks: any role ticked forces the person
  // selected; clearing the last role restores the value from before the first role was ticked.
  // Compulsory (locked) rows stay as-is.
  //
  // Compared against the explicit selection state rather than isSelected(), which already reports
  // true off the role flags the caller has just mutated: that comparison never differs, so ticking
  // a role would emit nothing and the parent's selected set - which decides what is persisted -
  // would miss the person even though the row renders as ticked.
  private syncSelectWithRoles(person: KybPerson) {
    if (this.isSelectLocked(person)) {
      return;
    }
    const stakeholderId = person.stakeholderId;
    const shouldSelect = person.isGuarantor || person.isSigner;
    const currentlySelected = this.selectionOverrides.get(stakeholderId)
      ?? this.savedOrDefaultSelected(person);
    let nextSelected = currentlySelected;
    if (shouldSelect) {
      if (!this.selectionBeforeRoles.has(stakeholderId)) {
        this.selectionBeforeRoles.set(stakeholderId, currentlySelected);
      }
      nextSelected = true;
    } else {
      nextSelected = this.selectionBeforeRoles.get(stakeholderId) ?? currentlySelected;
      this.selectionBeforeRoles.delete(stakeholderId);
    }
    if (nextSelected === currentlySelected) {
      return;
    }
    this.selectionOverrides.set(stakeholderId, nextSelected);
    this.personSelectedChange.emit({person, selected: nextSelected});
  }

  private emit(person: KybPerson) {
    this.personRoleChange.emit({
      stakeholderId: person.stakeholderId,
      isGuarantor: person.isGuarantor,
      isSigner: person.isSigner,
    });
    this.emitRowState(person);
  }

  // Emitted after syncSelectWithRoles, so the selection reported is the one the row now shows.
  private emitRowState(person: KybPerson) {
    this.personChange.emit({
      stakeholderId: person.stakeholderId,
      isSelected: this.isSelected(person),
      isGuarantor: this.isRoleLocked(person) ? true : person.isGuarantor,
      isSigner: this.signerChecked(person),
    });
  }
}
